import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Grid,
  FormHelperText,
  Alert,
  IconButton,
  InputAdornment,
  Popover
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MultiSectionDigitalClock } from '@mui/x-date-pickers/MultiSectionDigitalClock';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useSchedulesBreaksCreateMutation, useSalonsRetrieveQuery } from '../../../../services/GLOWClient';
import dayjs from 'dayjs';

const AddBreak = ({ open, onClose, onSuccess, schedule, teamMember, salon, dayOfWeek }) => {
  const [createBreak, { isLoading }] = useSchedulesBreaksCreateMutation();
  
  // Fetch salon details to get opening hours
  const { data: salonData, isLoading: salonLoading } = useSalonsRetrieveQuery(
    salon ? { salonId: salon } : undefined,
    { skip: !salon }
  );
  
  // Form state
  const [formData, setFormData] = useState({
    team_member: teamMember || '',
    salon: salon || '',
    day_of_week: dayOfWeek || '',
    start_time: null,
    end_time: null,
    description: ''
  });
  
  const [errors, setErrors] = useState({});
  
  // Time picker popover state
  const [timePickerAnchor, setTimePickerAnchor] = useState(null);
  const [activeTimePicker, setActiveTimePicker] = useState(null);
  
  // Initialize form data when props change
  useEffect(() => {
    setFormData({
      team_member: teamMember || '',
      salon: salon || '',
      day_of_week: dayOfWeek || '',
      start_time: null,
      end_time: null,
      description: ''
    });
  }, [teamMember, salon, dayOfWeek]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Handle time picker changes
  const handleTimeChange = (name, time) => {
    setFormData(prev => ({
      ...prev,
      [name]: time
    }));
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Close the popover
    setTimePickerAnchor(null);
    setActiveTimePicker(null);
  };
  
  // Handle direct time input changes (typing)
  const handleTimeInputChange = (name, value) => {
    const timeValue = value ? dayjs(`2023-01-01T${value}`) : null;
    setFormData(prev => ({
      ...prev,
      [name]: timeValue
    }));
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle time picker open
  const handleTimePickerOpen = (event, timeType) => {
    setTimePickerAnchor(event.currentTarget);
    setActiveTimePicker({ timeType });
  };
  
  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.start_time) {
      newErrors.start_time = 'Veuillez sélectionner une heure de début';
    }
    
    if (!formData.end_time) {
      newErrors.end_time = 'Veuillez sélectionner une heure de fin';
    }
    
    // Check if end time is after start time
    if (formData.start_time && formData.end_time) {
      const startMinutes = formData.start_time.hour() * 60 + formData.start_time.minute();
      const endMinutes = formData.end_time.hour() * 60 + formData.end_time.minute();
      
      if (endMinutes <= startMinutes) {
        newErrors.end_time = 'L\'heure de fin doit être après l\'heure de début';
      }
    }
    
    // Validate against schedule times if available
    if (schedule && formData.start_time && formData.end_time) {
      const scheduleStartTime = dayjs(`2023-01-01T${schedule.start_time}`);
      const scheduleEndTime = dayjs(`2023-01-01T${schedule.end_time}`);
      
      const breakStartMinutes = formData.start_time.hour() * 60 + formData.start_time.minute();
      const breakEndMinutes = formData.end_time.hour() * 60 + formData.end_time.minute();
      const scheduleStartMinutes = scheduleStartTime.hour() * 60 + scheduleStartTime.minute();
      const scheduleEndMinutes = scheduleEndTime.hour() * 60 + scheduleEndTime.minute();
      
      if (breakStartMinutes < scheduleStartMinutes) {
        newErrors.start_time = `L'heure de début doit être après ${schedule.start_time}`;
      }
      
      if (breakEndMinutes > scheduleEndMinutes) {
        newErrors.end_time = `L'heure de fin doit être avant ${schedule.end_time}`;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Format time for API
  const formatTimeForApi = (time) => {
    if (!time) return '';
    return time.format('HH:mm');
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      const createData = {
        team_member: Number(formData.team_member),
        salon: Number(formData.salon),
        day_of_week: formData.day_of_week,
        start_time: formatTimeForApi(formData.start_time),
        end_time: formatTimeForApi(formData.end_time),
        description: formData.description || null
      };
      
      const result = await createBreak({ breakRequest: createData }).unwrap();
      
      if (onSuccess) {
        onSuccess(result);
      }
      onClose();
    } catch (error) {
      console.error('Failed to create break:', error);
      
      // Handle API validation errors
      if (error.data) {
        const apiErrors = {};
        
        if (error.data.team_member) apiErrors.team_member = error.data.team_member.join(' ');
        if (error.data.salon) apiErrors.salon = error.data.salon.join(' ');
        if (error.data.day_of_week) apiErrors.day_of_week = error.data.day_of_week.join(' ');
        if (error.data.start_time) apiErrors.start_time = error.data.start_time.join(' ');
        if (error.data.end_time) apiErrors.end_time = error.data.end_time.join(' ');
        if (error.data.description) apiErrors.description = error.data.description.join(' ');
        if (error.data.non_field_errors) {
          apiErrors.general = error.data.non_field_errors.join(' ');
        }
        
        setErrors(apiErrors);
      }
    }
  };
  
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter une pause</DialogTitle>
        <DialogContent>
          {salonLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          )}
          
          {salonData && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Salon: {salonData.name} - Heures d'ouverture: {salonData.opening_hours?.ouverture || 'Non défini'} - {salonData.opening_hours?.fermeture || 'Non défini'}
            </Alert>
          )}
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Jour: {formData.day_of_week}
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    label="Heure de début"
                    value={formData.start_time ? formData.start_time.format('HH:mm') : ''}
                    size="small"
                    fullWidth
                    error={!!errors.start_time}
                    helperText={errors.start_time}
                    onChange={(e) => handleTimeInputChange('start_time', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccessTimeIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                  <IconButton
                    onClick={(e) => handleTimePickerOpen(e, 'start_time')}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.04)'
                      }
                    }}
                  >
                    <AccessTimeIcon />
                  </IconButton>
                </Box>
              </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Heure de fin"
                  value={formData.end_time ? formData.end_time.format('HH:mm') : ''}
                  size="small"
                  fullWidth
                  error={!!errors.end_time}
                  helperText={errors.end_time}
                  onChange={(e) => handleTimeInputChange('end_time', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTimeIcon />
                      </InputAdornment>
                    )
                  }}
                />
                <IconButton
                  onClick={(e) => handleTimePickerOpen(e, 'end_time')}
                  size="small"
                  disabled={isLoading}
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.04)'
                    }
                  }}
                >
                  <AccessTimeIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>

            <TextField
              fullWidth
              margin="normal"
              label="Description (optionnelle)"
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
              placeholder="Ex: Pause déjeuner"
              disabled={isLoading}
              error={!!errors.description}
              helperText={errors.description}
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary" 
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Time Picker Popover */}
      <Popover
        open={Boolean(timePickerAnchor)}
        anchorEl={timePickerAnchor}
        onClose={() => {
          setTimePickerAnchor(null);
          setActiveTimePicker(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MultiSectionDigitalClock
              value={activeTimePicker ? formData[activeTimePicker.timeType] : null}
              onChange={(newValue) => {
                if (activeTimePicker) {
                  handleTimeChange(activeTimePicker.timeType, newValue);
                }
              }}
            />
          </LocalizationProvider>
        </Box>
      </Popover>
    </>
  );
};

export default AddBreak;
